import { formatCurrency } from "../../../../../utils/formatCurrency";
import { escapeHtml } from "./securityHelpers";

/**
 * Generates the complete HTML for invoice printing
 * @param {Object} data - Invoice data object
 * @param {Function} t - Translation function
 * @param {Boolean} isPreview - If true, disables auto-print
 * @returns {String} Complete HTML document
 */
export const generateInvoiceHTML = (data, t, isPreview = false) => {
  // Extract lang early for error handling
  const lang = data?.lang || 'ar';
  
  try {
    // Validate required data
    if (!data.orderData || !data.order) {
      throw new Error('Order data is missing');
    }

    const {
      orderData,
      order,
      orderProducts,
      totalRefudProductsPrice,
      siteTitle,
      invoiceLogoUrl,
      companyEmail,
      companyPhone,
      formattedDate,
      isRTL,
      switchOrderStatus,
      handleShowTitle,
      switchLangLoading
    } = data;

    // Generate sections
    const customerInfo = generateCustomerInfo(orderData, t);
    const shippingInfo = generateShippingInfo(orderData, t);
    const productsRows = generateProductsRows(orderProducts, t, switchLangLoading, handleShowTitle);
    const paymentsTable = generatePaymentsTable(orderData, t);
    const footer = generateFooter(siteTitle, companyEmail, companyPhone, orderData, t, isRTL);
    const styles = generateStyles(isRTL);

    // Generate invoice filename
    const invoiceNumber = orderData?.invoice_no || orderData?.id || 'unknown';
    const invoiceDate = formattedDate.replace(/\s*\/\s*/g, '-'); // Replace / with -
    const filename = `Invoice-${invoiceNumber}-${invoiceDate}`.replace(/\s+/g, '_');

    return `
      <!DOCTYPE html>
      <html lang="${lang}" dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${filename}</title>
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css" rel="stylesheet">
        <style>${styles}</style>
      </head>
      <body>
        ${generateInvoiceHeader(orderData, order, siteTitle, invoiceLogoUrl, companyEmail, companyPhone, formattedDate, switchOrderStatus, t, isRTL)}
        ${generateInfoCards(siteTitle, companyEmail, companyPhone, customerInfo, shippingInfo, t)}
        ${generateProductsTable(productsRows, t)}
        ${generateSummaryTable(order, totalRefudProductsPrice, t)}
        ${paymentsTable}
        ${footer}
        <script>
          // Set document title for better filename when saving/printing
          document.title = '${filename}';
          
          ${isPreview ? 
            '// Preview mode - no auto-print' : 
            `window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };`
          }
        </script>
      </body>
      </html>
    `;
  } catch (error) {
    console.error('Error generating print content:', error);
    return generateErrorPage(t, lang);
  }
};

/**
 * Generate customer information section
 */
const generateCustomerInfo = (orderData, t) => {
  const safeText = (value, fallback = '') => escapeHtml(value || fallback);
  
  return orderData?.user ? `
    <div class="fw-semibold mb-1">${safeText(orderData.user.first_name)} ${safeText(orderData.user.last_name)}</div>
    <div class="text-muted">${safeText(orderData.user.email)}</div>
    ${orderData.user.phone ? `<div class="text-muted">${safeText(orderData.user.phone)}</div>` : ''}
  ` : `
    <div class="fw-semibold mb-1">${safeText(orderData?.guest_name, t("Guest Customer"))}</div>
    <div class="text-muted">${safeText(orderData?.guest_email, t("Not provided"))}</div>
    ${orderData?.phone ? `<div class="text-muted">${safeText(orderData.phone)}</div>` : ''}
  `;
};

/**
 * Generate shipping information section
 */
const generateShippingInfo = (orderData, t) => {
  const safeText = (value) => escapeHtml(value || '');
  
  if (!orderData?.shipping_address) {
    return `<div class="text-muted">${t("No shipping address provided")}</div>`;
  }

  const { address, city, state, country } = orderData.shipping_address;
  
  return `
    ${address ? `<div class="text-muted">${safeText(address)}</div>` : ''}
    <div class="text-muted">${[safeText(city), safeText(state)].filter(Boolean).join(", ")}</div>
    ${country ? `<div class="text-muted">${safeText(country)}</div>` : ''}
  `;
};

/**
 * Generate products table rows
 */
const generateProductsRows = (orderProducts, t, switchLangLoading, handleShowTitle) => {
  const safeText = (value) => escapeHtml(value || '');
  
  return orderProducts.map((product, index) => {
    const matchedVariant = product?.variants?.find(
      (variant) => variant.id == product?.pivot?.product_variant_id
    );
    
    const productTitle = switchLangLoading 
      ? safeText(t("Loading")) + '...' 
      : safeText(handleShowTitle(product.translations));
    
    const variantInfo = matchedVariant ? `
      <div class="text-muted small">${safeText(matchedVariant.sku)}</div>
      ${matchedVariant.attribute_values && matchedVariant.attribute_values.length > 0 ? 
        `<div class="text-muted small">${matchedVariant.attribute_values.map(attr => safeText(attr.value)).join(' / ')}</div>` 
        : ''}
    ` : `<span class="text-muted">${t("No Variant")}</span>`;

    return `
      <tr>
        <td style="text-align: center;">${index + 1}</td>
        <td>
          <div class="fw-semibold">${productTitle}</div>
          <div class="text-muted small">${safeText(product.sku)}</div>
        </td>
        <td>${variantInfo}</td>
        <td style="text-align: center;">${product.pivot?.qty || 0}</td>
        <td style="text-align: right;">${formatCurrency(product.pivot?.price || 0)}</td>
        <td style="text-align: center;">${parseInt(product.pivot?.refund_qty || 0) >= 1 ? `-${product.pivot.refund_qty}` : t("No Refund")}</td>
        <td style="text-align: right;">${formatCurrency(parseInt(product.pivot?.qty || 0) * parseInt(product.pivot?.price || 0))}</td>
      </tr>
    `;
  }).join('');
};

/**
 * Generate payments table
 */
const generatePaymentsTable = (orderData, t) => {
  if (!orderData?.payments || orderData.payments.length === 0) {
    return '';
  }

  const paymentsRows = orderData.payments.map(payment => `
    <tr>
      <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 12px;">${escapeHtml(payment.payment_gateway || t("Unknown"))}</td>
      <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 12px;"><code>${escapeHtml(payment.gateway_transaction_id || t("N/A"))}</code></td>
      <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 12px; text-align: right;">${formatCurrency(parseFloat(payment.amount || 0))}</td>
      <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 12px; text-align: center;">${escapeHtml(payment.currency || t("N/A"))}</td>
      <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 12px; text-align: center;">${escapeHtml(payment.status || t("Unknown"))}</td>
      <td style="border: 1px solid #e5e7eb; padding: 10px; font-size: 12px; text-align: center;">${payment.created_at ? new Date(payment.created_at).toLocaleDateString() : t("N/A")}</td>
    </tr>
  `).join('');

  return `
    <hr style="border: 0; border-top: 1px solid #e5e7eb; margin: 24px 0;" />
    <div class="fw-bold mb-3 text-uppercase" style="font-size: 13px;">${t("Payment Information")}</div>
    <table style="width: 100%; border-collapse: collapse;">
      <thead>
        <tr>
          <th style="border: 1px solid #e5e7eb; padding: 10px; background: #f8f9fa; font-size: 12px;">${t("Payment Gateway")}</th>
          <th style="border: 1px solid #e5e7eb; padding: 10px; background: #f8f9fa; font-size: 12px;">${t("Transaction ID")}</th>
          <th style="border: 1px solid #e5e7eb; padding: 10px; background: #f8f9fa; font-size: 12px;">${t("Amount")}</th>
          <th style="border: 1px solid #e5e7eb; padding: 10px; background: #f8f9fa; font-size: 12px;">${t("Currency")}</th>
          <th style="border: 1px solid #e5e7eb; padding: 10px; background: #f8f9fa; font-size: 12px;">${t("Status")}</th>
          <th style="border: 1px solid #e5e7eb; padding: 10px; background: #f8f9fa; font-size: 12px;">${t("Date")}</th>
        </tr>
      </thead>
      <tbody>
        ${paymentsRows}
      </tbody>
    </table>
  `;
};

/**
 * Generate invoice header section
 */
const generateInvoiceHeader = (orderData, order, siteTitle, invoiceLogoUrl, companyEmail, companyPhone, formattedDate, switchOrderStatus, t, isRTL) => {
  const safeText = (value) => escapeHtml(value || '');
  
  return `
    <div class="invoice-header">
      <div class="company-info">
        <img src="${invoiceLogoUrl}" alt="Logo" onerror="this.style.display='none'" />
        <h4 class="fw-bold mb-1">${safeText(siteTitle)}</h4>
        <div class="text-muted" style="font-size: 12px;">
          <div>${safeText(companyEmail)}</div>
          <div>${safeText(companyPhone)}</div>
        </div>
      </div>
      <div class="invoice-info">
        <h2>${t("Invoice")}</h2>
        <div style="margin-bottom: 8px;"><strong>${t("Invoice No")}:</strong> #${safeText(orderData?.invoice_no || orderData?.id)}</div>
        <div style="margin-bottom: 12px;"><strong>${t("Date")}:</strong> ${safeText(formattedDate)}</div>
        <div>
          <span class="badge">${switchOrderStatus(order.status)}</span>
          <span class="badge" style="margin-left: 8px;">${safeText(orderData?.payment_status || t("Unknown"))}</span>
        </div>
      </div>
    </div>
  `;
};

/**
 * Generate info cards section
 */
const generateInfoCards = (siteTitle, companyEmail, companyPhone, customerInfo, shippingInfo, t) => {
  const safeText = (value) => escapeHtml(value || '');
  
  return `
    <div class="info-cards">
      <div class="info-card">
        <h6>${t("From")}</h6>
        <div class="fw-semibold mb-1">${safeText(siteTitle)}</div>
        <div class="text-muted" style="font-size: 12px;">${safeText(companyEmail)}</div>
        <div class="text-muted" style="font-size: 12px;">${safeText(companyPhone)}</div>
      </div>
      <div class="info-card">
        <h6>${t("Bill To")}</h6>
        <div style="font-size: 12px;">${customerInfo}</div>
      </div>
      <div class="info-card">
        <h6>${t("Shipping Address")}</h6>
        <div style="font-size: 12px;">${shippingInfo}</div>
      </div>
    </div>
  `;
};

/**
 * Generate products table
 */
const generateProductsTable = (productsRows, t) => {
  return `
    <table>
      <thead>
        <tr>
          <th style="width: 5%; text-align: center;">#</th>
          <th style="width: 30%;">${t("Item")}</th>
          <th style="width: 20%;">${t("Variant")}</th>
          <th style="width: 10%; text-align: center;">${t("Quantity")}</th>
          <th style="width: 13%; text-align: right;">${t("Price")}</th>
          <th style="width: 10%; text-align: center;">${t("Refund")}</th>
          <th style="width: 12%; text-align: right;">${t("Total")}</th>
        </tr>
      </thead>
      <tbody>
        ${productsRows}
      </tbody>
    </table>
  `;
};

/**
 * Generate summary table
 */
const generateSummaryTable = (order, totalRefudProductsPrice, t) => {
  return `
    <table class="summary-table">
      <tbody>
        <tr>
          <td class="fw-semibold">${t("Subtotal")}</td>
          <td style="text-align: right;">${formatCurrency(parseFloat(order.total_price || 0))}</td>
        </tr>
        <tr>
          <td>${t("Discount")}</td>
          <td style="text-align: right;">-${formatCurrency(parseFloat(order.discount) || 0)}</td>
        </tr>
        <tr>
          <td>${t("Shipping")}</td>
          <td style="text-align: right;">${parseFloat(order.shipping_amount || 0) >= 1 ? formatCurrency(parseFloat(order.shipping_amount)) : '-'}</td>
        </tr>
        <tr>
          <td>${t("TAX")}</td>
          <td style="text-align: right;">${formatCurrency(parseFloat(order.tax_fee_amount || 0))}</td>
        </tr>
        ${parseFloat(totalRefudProductsPrice) > 0 ? `
          <tr>
            <td>${t("Total Refund")}</td>
            <td style="text-align: right; color: #dc3545;">-${formatCurrency(parseFloat(totalRefudProductsPrice))}</td>
          </tr>
        ` : ''}
        <tr>
          <td>${t("Total")}</td>
          <td style="text-align: right;">${formatCurrency(parseFloat(order.payable_amount || 0))}</td>
        </tr>
      </tbody>
    </table>
  `;
};

/**
 * Generate footer section
 */
const generateFooter = (siteTitle, companyEmail, companyPhone, orderData, t, isRTL) => {
  const safeText = (value) => escapeHtml(value || '');
  
  return `
    <div style="margin-top: 50px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; color: #6c757d; font-size: 11px;">
      <div style="margin-bottom: 10px;">
        <strong>${safeText(siteTitle)}</strong>
      </div>
      <div style="margin-bottom: 8px;">
        ${safeText(companyEmail)} | ${safeText(companyPhone)}
      </div>
      <div style="margin-top: 15px; font-size: 13px; color: #333;">
        ${t("Thank you for your business!")}
      </div>
      ${orderData?.notes ? `
        <div style="margin-top: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px; text-align: ${isRTL ? 'right' : 'left'};">
          <strong style="color: #333;">${t("Notes")}:</strong>
          <p style="margin: 8px 0 0 0; color: #555;">${safeText(orderData.notes)}</p>
        </div>
      ` : ''}
    </div>
  `;
};

/**
 * Generate CSS styles
 */
const generateStyles = (isRTL) => {
  return `
    @page {
      size: A4;
      margin: 20mm;
    }
    body {
      font-family: ${isRTL ? "'Cairo', 'Tajawal', " : ""}'Arial', sans-serif;
      font-size: 14px;
      line-height: 1.6;
      color: #333;
      background: white;
      padding: 20px;
      direction: ${isRTL ? 'rtl' : 'ltr'};
      text-align: ${isRTL ? 'right' : 'left'};
    }
    .invoice-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 30px;
      border-bottom: 2px solid #333;
      padding-bottom: 20px;
    }
    .company-info img {
      max-width: 180px;
      margin-bottom: 15px;
      object-fit: contain;
    }
    .company-info img[src=""], .company-info img:not([src]) {
      display: none;
    }
    .invoice-info {
      text-align: ${isRTL ? 'left' : 'right'};
    }
    [dir="rtl"] .invoice-info {
      text-align: left;
    }
    [dir="ltr"] .invoice-info {
      text-align: right;
    }
    .invoice-info h2 {
      font-size: 32px;
      font-weight: bold;
      margin-bottom: 10px;
      text-transform: uppercase;
    }
    .info-cards {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
      margin-bottom: 30px;
    }
    .info-card {
      border: 1px solid #e5e7eb;
      padding: 15px;
      border-radius: 8px;
    }
    .info-card h6 {
      font-size: 11px;
      font-weight: bold;
      text-transform: uppercase;
      margin-bottom: 10px;
      color: #6c757d;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 30px;
    }
    th, td {
      border: 1px solid #e5e7eb;
      padding: 12px;
      text-align: left;
    }
    thead th {
      background-color: #f8f9fa;
      font-weight: bold;
      font-size: 12px;
    }
    tbody td {
      font-size: 13px;
    }
    .summary-table {
      width: 50%;
      margin-left: auto;
      border: none;
    }
    .summary-table td {
      border: none;
      padding: 8px 0;
    }
    .summary-table tr:last-child td {
      font-size: 16px;
      font-weight: bold;
      border-top: 2px solid #333;
      padding-top: 12px;
    }
    .badge {
      display: inline-block;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 11px;
      border: 1px solid #ccc;
    }
    .text-muted {
      color: #6c757d;
    }
    .fw-semibold {
      font-weight: 600;
    }
    .fw-bold {
      font-weight: 700;
    }
    @media print {
      body {
        padding: 0;
      }
      .no-print {
        display: none;
      }
    }
  `;
};

/**
 * Generate error page when invoice generation fails
 */
const generateErrorPage = (t, lang) => {
  const errorTitle = `Invoice-Error-${new Date().toISOString().split('T')[0]}`;
  
  return `
    <!DOCTYPE html>
    <html lang="${lang}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${errorTitle}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          margin: 0;
          background: #f5f5f5;
        }
        .error-container {
          background: white;
          padding: 40px;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          text-align: center;
          max-width: 500px;
        }
        .error-icon {
          font-size: 48px;
          color: #dc3545;
          margin-bottom: 20px;
        }
        h2 {
          color: #333;
          margin-bottom: 15px;
        }
        p {
          color: #666;
          line-height: 1.6;
        }
        button {
          margin-top: 20px;
          padding: 10px 30px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-size: 14px;
        }
        button:hover {
          background: #0056b3;
        }
      </style>
    </head>
    <body>
      <div class="error-container">
        <div class="error-icon">⚠️</div>
        <h2>${t("Error generating invoice")}</h2>
        <p>${t("An error occurred while preparing your invoice for printing. Please try again or contact support if the problem persists.")}</p>
        <button onclick="window.close()">${t("Close Window")}</button>
      </div>
      <script>
        document.title = '${errorTitle}';
      </script>
    </body>
    </html>
  `;
};

